import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Search, LayoutGrid, UserCheck, Plus, ChevronLeft, Timer, ChevronDown, Target, Lock, Check, X, MoreVertical, Edit3, Trash2, Unlock, Filter } from 'lucide-react';
import { ToolSelect } from '../../DashboardV2/components/ToolSelect';
import { Dropdown, Select, ConfigProvider, Tooltip, Pagination, Modal, DatePicker, Calendar, Input } from 'antd';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { useTheme } from '../../../contexts/ThemeContext';
import * as goalsApi from '../../../services/goalsPerformanceApi';
import { Avatar } from '../../DashboardV2/components/primitives';
import TeamMembersHeader from './TeamMembersHeader';
import TeamMembersTable from './TeamMembersTable';
import MonthlyRoadmap from './MonthlyRoadmap';
import GoalsTable from './GoalsTable';
import SoftSkillsSection from './SoftSkillsSection';
import ReviewSummary from './ReviewSummary';
import GoalsDashboard from './GoalsDashboard';
import GoalsDetailView from './GoalsDetailView';
import GoalsStatChips from './GoalsStatChips';
import { GoalsTableSkeleton, SoftSkillsSkeleton, ReviewSummarySkeleton } from './GoalsSkeleton';
import { motion, AnimatePresence } from 'framer-motion';

const ManagerView = ({
  managerTab,
  setManagerTab,
  teamMembers,
  teamStats = null,
  searchQuery,
  setSearchQuery,
  selectedMember,
  setSelectedMember,
  memberReportMonth,
  setMemberReportMonth,
  selectedYear,
  setSelectedYear,
  months,
  roadmapStartIndex,
  onPrevRoadmap,
  onNextRoadmap,
  onMonthClick,
  currentMonthGoals,
  softSkillsData,
  reviewSummary,
  isReviewSummaryExpanded,
  onToggleReviewSummary,
  onReviewChange,
  onAddGoal,
  onApproveGoal,
  onRejectGoal,
  onEditGoal,
  onDeleteGoal,
  onSetAchievedPercent,
  bannerConfig,
  onSoftSkillRatingChange,
  canEditEmployeeRating = false,
  canEditManagerRating = false,
  canEditEmployeeResponse = false,
  canEditManagerResponse = true,
  onAddAchievement,
  achievements = [],
  isManagerDetailView,
  setIsManagerDetailView,
  minGoals = 0,
  maxGoals = 0,
  criticalRequired = 0,
  minWeightage = 10,
  maxWeightage = 100,
  inlineNewGoal,
  setInlineNewGoal,
  editingGoalId,
  setEditingGoalId,
  onSaveNewGoal,
  onSaveEditGoal,
  onCancelEdit,
  managerUserId = null,
  loading = false,
  softSkillsLoading = false,
  reviewSummaryLoading = false,
  currentCycle = null,
  onSubmitGoals = null,
  scoreRanges = [],
  isSubmissionWindowActive = false,
  isReviewWindowActive = false,
  isEvaluationWindowActive = false,
  isViewedMonthCycleEvaluated = false,
  isOrgAdmin = false,
  isAutonomousMode = false,
  badgeRanges = [],
  onToggleCritical,
  aiGoalEnabled = false,
  onToggleAiGoal = null,
  softSkillsEnabled = true,
  streakIconUrl = null,
  allCycles = [],
  projectsList = [],
  pagination = { total: 0, totalPages: 1 },
  currentPage = 1,
  onPageChange = null,
  goalFilter = 'all',
  setGoalFilter = () => {},
  submissionCounts = { all: 0, submitted: 0, notSubmitted: 0 },
  reporteeScope = 'direct',
  setReporteeScope = () => {},
  hasIndirectReports = false,
  reviewCompletedAt = null,
  onMarkReviewComplete = null,
  isMarkingReviewComplete = false,
  onSelfEvaluate = null,
  onVerifyGoal = null,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  // State for previous month data
  const [previousMonthData, setPreviousMonthData] = useState({
    streak: null,
    rank: null,
    score: null
  });

  // Org-admin: per-member submission reopen after the org-wide window ended
  const [memberReopen, setMemberReopen] = useState(null);
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [reopenUntil, setReopenUntil] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenBusy, setReopenBusy] = useState(false);

  const memberUserId = selectedMember?._id || selectedMember?.id || null;
  const cycleIdForReopen = currentCycle?._id || null;

  const loadMemberReopen = useCallback(async () => {
    if (!isOrgAdmin || !cycleIdForReopen || !memberUserId) {
      setMemberReopen(null);
      return;
    }
    try {
      const res = await goalsApi.getSubmissionException(cycleIdForReopen, memberUserId);
      setMemberReopen(res?.data || null);
    } catch {
      setMemberReopen(null);
    }
  }, [isOrgAdmin, cycleIdForReopen, memberUserId]);

  useEffect(() => {
    loadMemberReopen();
  }, [loadMemberReopen]);

  const orgSubmissionOpen = useMemo(() => {
    if (!currentCycle?.submissionStart || !currentCycle?.submissionEnd) return false;
    const now = Date.now();
    const start = new Date(currentCycle.submissionStart).getTime();
    const end = new Date(currentCycle.submissionEnd);
    end.setHours(23, 59, 59, 999);
    return now >= start && now <= end.getTime();
  }, [currentCycle]);

  const showReopenControls = isOrgAdmin && !!selectedMember && !!cycleIdForReopen && !orgSubmissionOpen;

  const handleConfirmReopen = async () => {
    if (!cycleIdForReopen || !memberUserId || !reopenUntil) {
      toast.error('Pick an end date/time');
      return;
    }
    if (reopenUntil.valueOf() <= Date.now()) {
      toast.error('End time must be in the future');
      return;
    }
    setReopenBusy(true);
    try {
      const res = await goalsApi.reopenSubmissionForMember(cycleIdForReopen, {
        userId: memberUserId,
        reopenedUntil: reopenUntil.toISOString(),
        reason: reopenReason.trim() || undefined,
      });
      setMemberReopen(res?.data || null);
      setReopenModalOpen(false);
      setReopenReason('');
      setReopenUntil(null);
      toast.success(`Submission reopened for ${selectedMember?.name || 'member'}`);
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || err?.response?.data?.message || err.message || 'Failed to reopen');
    } finally {
      setReopenBusy(false);
    }
  };

  const handleRevokeReopen = async () => {
    if (!cycleIdForReopen || !memberUserId) return;
    setReopenBusy(true);
    try {
      await goalsApi.revokeSubmissionException(cycleIdForReopen, memberUserId);
      setMemberReopen(null);
      toast.success('Submission reopen closed');
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || err?.response?.data?.message || err.message || 'Failed to close reopen');
    } finally {
      setReopenBusy(false);
    }
  };

  // Filter months: only show months with score > 0, active, or upcoming
  const filteredRoadmapMonths = (months || []).filter(m => {
    if (m.score != null && m.score !== undefined && parseFloat(m.score) > 0) return true;
    if (m.status === 'active' || m.status === 'upcoming') return true;
    return false;
  });

  // Top-level grid always shows direct reports (server-side scope=direct).
  // Indirect reports are reachable by expanding a row in the table.
  const filteredMembers = (teamMembers || [])
    .sort((a, b) => {
      // Check if member has submitted goals
      const aHasGoals = a.goals && a.goals !== '0/0' && a.goals !== '0' ? 1 : 0;
      const bHasGoals = b.goals && b.goals !== '0/0' && b.goals !== '0' ? 1 : 0;

      // Sort: members with goals first (descending), then others
      if (aHasGoals !== bHasGoals) {
        return bHasGoals - aHasGoals;
      }

      // Within same group, sort by name alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });

  // Check if all goals are approved or rejected
  const allGoalsReviewed = useMemo(() => {
    if (!currentMonthGoals || currentMonthGoals.length === 0) return false;
    const actualGoals = currentMonthGoals.filter(g => !g.isNewGoal && !g.isEmptyRow);
    if (actualGoals.length === 0) return false;
    return actualGoals.every(g => {
      const status = (g.status || '').toString().trim().toUpperCase();
      return status === 'APPROVED' || status === 'REJECTED' || status === 'COMPLETED';
    });
  }, [currentMonthGoals]);

  // Format date helper function
  const formatDate = (date) => {
    if (!date) return null;
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Get review end date or evaluation end date
  const getDeadlineDate = () => {
    if (allGoalsReviewed && currentCycle?.evaluationEnd) {
      return formatDate(currentCycle.evaluationEnd);
    } else if (currentCycle?.reviewEnd) {
      return formatDate(currentCycle.reviewEnd);
    }
    return null;
  };

  // Get days until review window opens
  const getDaysUntilReview = () => {
    if (!currentCycle?.reviewStart) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const reviewStart = new Date(currentCycle.reviewStart);
    reviewStart.setHours(0, 0, 0, 0);
    const diff = Math.ceil((reviewStart - today) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : null;
  };

  // Get deadline message
  const getDeadlineMessage = () => {
    if (!isReviewWindowActive) {
      const days = getDaysUntilReview();
      if (days !== null) {
        return `In ${days} day${days === 1 ? '' : 's'}, you can review and approve the goals`;
      }
    }

    // Get date range for evaluation/review window
    let startDate = null;
    let endDate = null;

    if (allGoalsReviewed && currentCycle?.evaluationStart && currentCycle?.evaluationEnd) {
      if (parseFloat(calculateScoreFromGoals) > 0) {
        return 'Evaluation Done';
      }
      startDate = formatDate(currentCycle.evaluationStart);
      endDate = formatDate(currentCycle.evaluationEnd);
      return `EVALUATE GOALS FROM ${startDate} TO ${endDate}`;
    } else if (currentCycle?.reviewStart && currentCycle?.reviewEnd) {
      startDate = formatDate(currentCycle.reviewStart);
      endDate = formatDate(currentCycle.reviewEnd);
      return `APPROVE GOALS FROM ${startDate} TO ${endDate}`;
    } else {
      // Fallback to single date if date range not available
      return `APPROVE GOALS BY ${getDeadlineDate() || 'N/A'}`;
    }
  };

  // Calculate score from currentMonthGoals (same logic as GoalsDetailView/Employee view)
  const calculateScoreFromGoals = useMemo(() => {
    if (!currentMonthGoals || currentMonthGoals.length === 0) {
      return '0.0';
    }

    // Only APPROVED goals (same as employee view)
    const approvedGoals = currentMonthGoals.filter(g => g.status === 'APPROVED');

    if (approvedGoals.length === 0) {
      return '0.0';
    }

    // Prefer goal.score first, fallback to achievedPercent (same as employee view)
    const goalsWithScores = approvedGoals.map(goal => {
      let goalScore = null;

      if (goal.score != null && goal.score !== undefined && !isNaN(goal.score)) {
        goalScore = parseFloat(goal.score);
      } else if (goal.achievedPercent != null && goal.achievedPercent !== undefined) {
        const p = Math.max(0, Math.min(100, goal.achievedPercent || goal.achieved || 0));
        if (p > 0) goalScore = Math.min(10, Math.max(1, Math.floor(p / 10) + (p % 10 > 0 ? 1 : 0)));
      }

      return {
        score: goalScore,
        weightage: goal.weightage || goal.weight || 0
      };
    }).filter(g => g.score != null && g.score !== undefined && g.weightage > 0);

    if (goalsWithScores.length === 0) {
      return '0.0';
    }

    let totalWeightedScore = 0;
    let totalWeight = 0;

    goalsWithScores.forEach(goal => {
      totalWeightedScore += goal.score * goal.weightage;
      totalWeight += goal.weightage;
    });

    if (totalWeight === 0) {
      return '0.0';
    }

    const finalScore = totalWeightedScore / totalWeight;
    return (Math.floor(finalScore * 10) / 10).toFixed(1); // Truncate like employee view
  }, [currentMonthGoals, selectedMember?.score]);

  // Calculate goals count from currentMonthGoals (real-time)
  // Only count goals that have been evaluated (have scores)
  const calculateGoalsCount = useMemo(() => {
    if (!currentMonthGoals || currentMonthGoals.length === 0) {
      return '0/0';
    }

    // Filter out new goals and empty rows
    const actualGoals = currentMonthGoals.filter(g => !g.isNewGoal && !g.isEmptyRow);
    const totalGoals = actualGoals.length;

    // Count only evaluated goals (goals with scores or achievedPercent)
    const evaluatedGoals = actualGoals.filter(g => {
      const hasScore = g.score != null && g.score !== undefined && !isNaN(g.score);
      const hasAchievedPercent = g.achievedPercent != null && g.achievedPercent !== undefined && !isNaN(g.achievedPercent) && g.achievedPercent > 0;
      return hasScore || hasAchievedPercent;
    });
    const evaluatedCount = evaluatedGoals.length;

    return `${evaluatedCount}/${totalGoals}`;
  }, [currentMonthGoals, selectedMember?.goals]);

  // Helper function to get previous month
  const getPreviousMonth = (currentMonth, currentYear) => {
    if (!currentMonth || !months || months.length === 0) return null;

    // months is an array of objects with 'name' property
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];

    // Find current month index in monthNames array
    const currentMonthIndex = monthNames.findIndex(m =>
      m.toLowerCase() === (typeof currentMonth === 'string' ? currentMonth : currentMonth.name || '').toLowerCase()
    );

    if (currentMonthIndex === -1) return null;

    let prevMonthIndex = currentMonthIndex - 1;
    let prevYear = currentYear;

    if (prevMonthIndex < 0) {
      prevMonthIndex = monthNames.length - 1;
      prevYear = currentYear - 1;
    }

    return {
      month: monthNames[prevMonthIndex],
      year: prevYear
    };
  };

  // Idempotent setter — only flips state when values actually change.
  // Without this, setPreviousMonthData({...}) creates a new object reference on
  // every effect run, which (combined with prop refs that change on parent
  // re-renders) produces an infinite render loop.
  const updatePrevMonthDataIfChanged = (next) => {
    setPreviousMonthData(prev => {
      if (
        prev.streak === next.streak &&
        prev.rank === next.rank &&
        prev.score === next.score
      ) {
        return prev;
      }
      return next;
    });
  };

  // Stable signature for memberReportMonth + selectedMember + selectedYear so
  // the effect doesn't re-fire on every parent re-render that just hands us a
  // new array reference for months/allCycles/scoreRanges/badgeRanges.
  const selectedMemberId = selectedMember?._id || selectedMember?.id || null;
  const allCyclesKey = useMemo(() => {
    if (!Array.isArray(allCycles)) return '';
    return allCycles.map(c => `${c?._id || ''}:${c?.month || ''}:${c?.year || ''}`).join('|');
  }, [allCycles]);
  const scoreRangesKey = useMemo(() => {
    if (!Array.isArray(scoreRanges)) return '';
    return scoreRanges.map(r => `${r?.min}-${r?.max}-${r?.score}`).join('|');
  }, [scoreRanges]);
  const badgeRangesKey = useMemo(() => {
    if (!Array.isArray(badgeRanges)) return '';
    return badgeRanges.map(r => `${r?.label}-${r?.min}-${r?.max}-${r?.lessThan ? 1 : 0}`).join('|');
  }, [badgeRanges]);

  // Fetch previous month data for streak and rank
  useEffect(() => {
    let cancelled = false;

    const fetchPreviousMonthData = async () => {
      if (!memberReportMonth || !selectedMemberId || !allCycles || allCycles.length === 0 || !months || months.length === 0) {
        if (!cancelled) updatePrevMonthDataIfChanged({ streak: null, rank: null, score: null });
        return;
      }

      const currentYear = parseInt(selectedYear) || new Date().getFullYear();
      const prevMonthInfo = getPreviousMonth(memberReportMonth, currentYear);

      if (!prevMonthInfo) {
        if (!cancelled) updatePrevMonthDataIfChanged({ streak: null, rank: null, score: null });
        return;
      }

      // Find previous month's cycle
      const prevCycle = allCycles.find(c => {
        if (!c || !c.month) return false;
        const cycleMonth = (c.month || '').toString().trim();
        const prevMonth = (prevMonthInfo.month || '').toString().trim();
        const monthMatch = cycleMonth.toLowerCase() === prevMonth.toLowerCase();

        if (c.year != null) {
          const cycleYear = typeof c.year === 'number' ? c.year : parseInt(c.year);
          return monthMatch && cycleYear === prevMonthInfo.year;
        }
        return monthMatch;
      });

      if (!prevCycle?._id) {
        if (!cancelled) updatePrevMonthDataIfChanged({ streak: null, rank: null, score: null });
        return;
      }

      try {
        // Fetch previous month's goals
        const goalsResponse = await goalsApi.getGoals({ cycleId: prevCycle._id, userId: selectedMemberId });
        if (cancelled) return;
        if (!goalsResponse.success || !goalsResponse.data || goalsResponse.data.length === 0) {
          updatePrevMonthDataIfChanged({ streak: null, rank: null, score: null });
          return;
        }

        const prevGoals = goalsResponse.data;

        // Calculate previous month's score (same logic as calculateScoreFromGoals)
        const approvedGoals = prevGoals.filter(g => {
          const status = (g.status || '').toString().trim().toUpperCase();
          return status === 'APPROVED' || status === 'COMPLETED';
        });

        if (approvedGoals.length === 0) {
          updatePrevMonthDataIfChanged({ streak: null, rank: null, score: null });
          return;
        }

        const goalsWithScores = approvedGoals.map(goal => {
          let goalScore = null;
          if (goal.score != null && goal.score !== undefined && !isNaN(goal.score)) {
            goalScore = parseFloat(goal.score);
          } else if (goal.achievedPercent != null && goal.achievedPercent !== undefined) {
            const achieved = goal.achievedPercent || goal.achieved || 0;
            const p = Math.max(0, Math.min(100, achieved));
            if (scoreRanges && scoreRanges.length > 0) {
              const range = scoreRanges.find(r => p >= r.min && p <= r.max);
              if (range) {
                goalScore = range.score;
              }
            }
            if (goalScore === null) {
              goalScore = Math.min(10, Math.max(1, Math.floor(p / 10) + (p % 10 > 0 ? 1 : 0)));
            }
          }
          return {
            score: goalScore,
            weightage: goal.weightage || goal.weight || 0
          };
        }).filter(g => g.score != null && g.score !== undefined && g.weightage > 0);

        if (goalsWithScores.length === 0) {
          updatePrevMonthDataIfChanged({ streak: null, rank: null, score: null });
          return;
        }

        let totalWeightedScore = 0;
        let totalWeight = 0;
        goalsWithScores.forEach(goal => {
          totalWeightedScore += goal.score * goal.weightage;
          totalWeight += goal.weightage;
        });

        if (totalWeight === 0) {
          updatePrevMonthDataIfChanged({ streak: null, rank: null, score: null });
          return;
        }

        const prevScore = totalWeightedScore / totalWeight;
        const finalPrevScore = Math.round(prevScore * 10) / 10;

        // Calculate previous month's rank from score
        let prevRank = null;
        if (finalPrevScore > 0 && badgeRanges && badgeRanges.length > 0) {
          for (const badgeRange of badgeRanges) {
            const min = parseFloat(badgeRange.min) || 0;
            const max = parseFloat(badgeRange.max) || 0;
            if (badgeRange.lessThan) {
              if (finalPrevScore < max) {
                prevRank = badgeRange.label;
                break;
              }
            } else {
              if (finalPrevScore >= min && finalPrevScore <= max) {
                prevRank = badgeRange.label;
                break;
              }
            }
          }

          // If no exact match, find closest
          if (!prevRank) {
            let closestBadge = null;
            let minDistance = Infinity;
            for (const badgeRange of badgeRanges) {
              const min = parseFloat(badgeRange.min) || 0;
              const max = parseFloat(badgeRange.max) || 0;
              if (badgeRange.lessThan) {
                if (finalPrevScore >= max) {
                  const distance = finalPrevScore - max;
                  if (distance < minDistance) {
                    minDistance = distance;
                    closestBadge = badgeRange.label;
                  }
                }
              } else {
                let distance;
                if (finalPrevScore < min) {
                  distance = min - finalPrevScore;
                } else if (finalPrevScore > max) {
                  distance = finalPrevScore - max;
                } else {
                  distance = 0;
                }
                if (distance < minDistance) {
                  minDistance = distance;
                  closestBadge = badgeRange.label;
                }
              }
            }
            prevRank = closestBadge || 'Beginner';
          }
        }

        // For streak, check if previous month was fully evaluated
        // If previous month has score > 0, it means it was evaluated, so streak = 1
        // Otherwise streak = 0
        const prevStreak = finalPrevScore > 0 ? 1 : 0;

        if (!cancelled) {
          updatePrevMonthDataIfChanged({
            streak: prevStreak,
            rank: prevRank,
            score: finalPrevScore
          });
        }
      } catch (error) {
        console.error('Error fetching previous month data:', error);
        if (!cancelled) updatePrevMonthDataIfChanged({ streak: null, rank: null, score: null });
      }
    };

    fetchPreviousMonthData();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberReportMonth, selectedMemberId, selectedYear, allCyclesKey, scoreRangesKey, badgeRangesKey]);

  // Removed console.log for filteredMembers

  const teamAvgScore = teamMembers && teamMembers.length > 0
    ? (
      Math.floor((teamMembers.reduce((acc, m) => {
        const score = parseFloat(m.score || 0);
        return acc + (isNaN(score) ? 0 : score);
      }, 0) / teamMembers.length) * 10) / 10
    ).toFixed(1)
    : '0.0';

  if (selectedMember) {
    // Compute summary stats for inline display
    const streakValue = (() => {
      const prevStreak = previousMonthData.streak !== null ? previousMonthData.streak : 0;
      const actualGoals = (currentMonthGoals || []).filter(g => !g.isNewGoal && !g.isEmptyRow && g.status !== 'REJECTED');
      const allApproved = actualGoals.length > 0 && actualGoals.every(g => g.status === 'APPROVED');
      let totalStreak = prevStreak;
      if (allApproved) totalStreak = prevStreak + 1;
      return totalStreak > 0 ? `${totalStreak} ${totalStreak === 1 ? 'Mo' : 'Mos'}` : '—';
    })();

    const rankValue = (() => {
      const score = parseFloat(calculateScoreFromGoals || '0') || 0;
      if (score <= 0) return previousMonthData.rank || '—';
      if (badgeRanges && badgeRanges.length > 0) {
        for (const badgeRange of badgeRanges) {
          const min = parseFloat(badgeRange.min) || 0;
          const max = parseFloat(badgeRange.max) || 0;
          if (badgeRange.lessThan) { if (score < max) return badgeRange.label; }
          else { if (score >= min && score <= max) return badgeRange.label; }
        }
        let closestBadge = null; let minDistance = Infinity;
        for (const badgeRange of badgeRanges) {
          const min = parseFloat(badgeRange.min) || 0;
          const max = parseFloat(badgeRange.max) || 0;
          let distance;
          if (badgeRange.lessThan) { distance = score >= max ? score - max : Infinity; }
          else { distance = score < min ? min - score : score > max ? score - max : 0; }
          if (distance < minDistance) { minDistance = distance; closestBadge = badgeRange.label; }
        }
        if (closestBadge) return closestBadge;
      }
      return 'Beginner';
    })();

    // Individual Member Report View
    return (
      <motion.div
        className="space-y-4 pb-12"
        style={{ fontFamily: '-apple-system, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif' }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, type: "spring", stiffness: 100 }}
      >
        {/* Member Header Row - Back + Name */}
        <motion.div
          className="flex items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.35, type: "spring", stiffness: 120 }}
        >
          <motion.button
            onClick={() => setSelectedMember(null)}
            className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${isDark
              ? 'bg-white/5 border-white/10 text-white/60 hover:text-blue-400 hover:border-blue-500/30'
              : 'bg-white border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft size={16} strokeWidth={2.5} />
          </motion.button>
          <Avatar user={selectedMember} name={selectedMember.name} size={36} />
          <div className="flex flex-col text-left flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className={`text-[15px] font-semibold leading-tight ${isDark ? '!text-white' : 'text-slate-900'}`}>
                {selectedMember.name}
              </h2>
              <span className={`text-xs font-medium ${isDark ? 'text-white/45' : 'text-slate-500'}`}>
                {selectedMember.role}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <div className={`flex items-center gap-1 px-1.5 py-px rounded ${isDark
                ? allGoalsReviewed ? 'text-blue-400 bg-blue-500/15' : 'text-amber-400 bg-amber-500/15'
                : allGoalsReviewed ? 'text-blue-600 bg-blue-50' : 'text-amber-700 bg-amber-50'
                }`}>
                <Timer size={10} />
                <span className="text-[11px] font-medium">
                  {getDeadlineMessage()}
                </span>
              </div>
              {memberReopen?.reopenedUntil && (
                <div className={`flex items-center gap-1 px-1.5 py-px rounded ${isDark ? 'text-emerald-400 bg-emerald-500/15' : 'text-emerald-700 bg-emerald-50'}`}>
                  <Unlock size={10} />
                  <span className="text-[11px] font-medium">
                    Reopen until {dayjs(memberReopen.reopenedUntil).format('D MMM, h:mm A')}
                  </span>
                </div>
              )}
            </div>
          </div>
          {showReopenControls && (
            <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
              {memberReopen?.reopenedUntil ? (
                <button
                  type="button"
                  disabled={reopenBusy}
                  onClick={handleRevokeReopen}
                  className={`h-8 px-2.5 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${isDark
                    ? 'bg-white/5 border-white/10 text-white/70 hover:text-red-300 hover:border-red-500/30'
                    : 'bg-white border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-300'
                    }`}
                >
                  <Lock size={12} />
                  Close reopen
                </button>
              ) : (
                <button
                  type="button"
                  disabled={reopenBusy}
                  onClick={() => {
                    setReopenUntil(dayjs().add(1, 'day').hour(23).minute(59).second(59));
                    setReopenModalOpen(true);
                  }}
                  className={`h-8 px-2.5 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${isDark
                    ? 'bg-blue-500/15 border-blue-500/30 text-blue-300 hover:bg-blue-500/25'
                    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                >
                  <Unlock size={12} />
                  Reopen submission
                </button>
              )}
            </div>
          )}
        </motion.div>

        <Modal
          open={reopenModalOpen}
          onCancel={() => !reopenBusy && setReopenModalOpen(false)}
          onOk={handleConfirmReopen}
          okText="Reopen"
          confirmLoading={reopenBusy}
          title={`Reopen submission — ${selectedMember?.name || ''}`}
          destroyOnClose
        >
          <p className="text-sm text-slate-500 mb-3">
            This member can add and submit goals until the time you pick. Everyone else stays closed.
          </p>
          <div className="mb-4">
            <div className="text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Open until</div>
            {reopenUntil && (
              <div className="text-sm font-medium text-slate-700 mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                {reopenUntil.format('DD MMM YYYY')} — closes at 11:59 PM
              </div>
            )}
            <div className="border border-slate-200 rounded-lg overflow-hidden goals-reopen-calendar">
              <Calendar
                fullscreen={false}
                value={reopenUntil || dayjs()}
                onSelect={(d) => {
                  if (d && !d.isBefore(dayjs().startOf('day'))) {
                    setReopenUntil(d.hour(23).minute(59).second(59).millisecond(0));
                  }
                }}
                disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))}
                headerRender={({ value, onChange }) => {
                  const month = value.format('MMM YYYY');
                  return (
                    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                      <button onClick={() => onChange(value.subtract(1, 'month'))} className="p-1 rounded hover:bg-slate-100 text-slate-500 text-xs">‹</button>
                      <span className="text-sm font-semibold text-slate-700">{month}</span>
                      <button onClick={() => onChange(value.add(1, 'month'))} className="p-1 rounded hover:bg-slate-100 text-slate-500 text-xs">›</button>
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Reason (optional)</div>
            <Input.TextArea
              rows={2}
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="e.g. On leave during submission window"
              maxLength={500}
            />
          </div>
        </Modal>

        {/* Stat strip — same GoalsStatChips used on the My Goals tab, so both
            surfaces read identically instead of maintaining a second copy. */}
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.35, type: "spring", stiffness: 120 }}
        >
          <GoalsStatChips
            bannerConfig={{ rankLabel: rankValue, currentStreak: streakValue }}
            employeeAvgScore={calculateScoreFromGoals}
            goalsCount={(currentMonthGoals || []).filter(g => !g.isNewGoal && !g.isEmptyRow).length}
          />
        </motion.div>

        {/* Compact Month Selector Strip — temporarily hidden per design feedback */}
        {false && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h3 className={`text-[13px] font-black uppercase tracking-wider leading-none ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                {memberReportMonth?.toUpperCase() || currentCycle?.month?.toUpperCase() || ''} {selectedYear} Goals
              </h3>
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                memberReportMonth === 'January'
                  ? isDark ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                  : isDark ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-50 text-blue-600'
              }`}>
                {memberReportMonth === 'January' ? 'APPROVED' : 'IN PROGRESS'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <ConfigProvider
                theme={{
                  token: {
                    borderRadius: 8,
                    controlHeight: 30,
                    colorBgContainer: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                    colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                    colorText: isDark ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
                    fontSize: 12,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    fontWeight: 600,
                  },
                }}
              >
                <Select
                  value={selectedYear}
                  onChange={(value) => setSelectedYear(value)}
                  className="w-[78px]"
                  style={{ width: 78 }}
                  options={[
                    { label: '2024', value: '2024' },
                    { label: '2025', value: '2025' },
                    { label: '2026', value: '2026' },
                  ]}
                />
              </ConfigProvider>
              <button
                onClick={onPrevRoadmap}
                disabled={roadmapStartIndex === 0}
                className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all ${roadmapStartIndex === 0
                  ? isDark ? 'border-white/10 text-white/20 cursor-not-allowed' : 'border-slate-100 text-slate-200 cursor-not-allowed'
                  : isDark ? 'border-white/20 text-white/60 hover:bg-white/10' : 'border-slate-200 text-slate-400 hover:bg-slate-50'
                }`}
              >
                <ChevronLeft size={16} strokeWidth={2.5} />
              </button>
              <button
                onClick={onNextRoadmap}
                disabled={roadmapStartIndex + 4 >= filteredRoadmapMonths.length}
                className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all ${roadmapStartIndex + 4 >= filteredRoadmapMonths.length
                  ? isDark ? 'border-white/10 text-white/20 cursor-not-allowed' : 'border-slate-100 text-slate-200 cursor-not-allowed'
                  : isDark ? 'border-white/20 text-white/60 hover:bg-white/10' : 'border-slate-200 text-slate-400 hover:bg-slate-50'
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            </div>
          </div>
          {/* Horizontal month pills - fill full width */}
          <div className="grid gap-2 pb-1" style={{ gridTemplateColumns: `repeat(${Math.min(filteredRoadmapMonths.slice(roadmapStartIndex, roadmapStartIndex + 6).length, 6)}, 1fr)` }}>
            {filteredRoadmapMonths.slice(roadmapStartIndex, roadmapStartIndex + 6).map((m, idx) => {
              const isSelected = memberReportMonth === m.name;
              const isUpcoming = m.status === 'upcoming';
              return (
                <motion.button
                  key={m.name}
                  onClick={() => { if (!isUpcoming) setMemberReportMonth(m.name); }}
                  disabled={isUpcoming}
                  className={`relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-left transition-all duration-200 ${
                    isSelected
                      ? isDark
                        ? 'bg-blue-500/20 border-blue-400/50 shadow-[0_0_20px_rgba(59,130,246,0.1)]'
                        : 'bg-blue-50 border-blue-300 shadow-sm'
                      : isUpcoming
                        ? isDark ? 'bg-white/[0.02] border-white/5 opacity-40 cursor-not-allowed' : 'bg-white/50 border-slate-100 opacity-40 cursor-not-allowed'
                        : m.status === 'completed'
                          ? isDark ? 'bg-white/[0.03] border-white/8 hover:bg-white/[0.06] cursor-pointer' : 'bg-white border-slate-100 hover:border-slate-200 cursor-pointer'
                          : isDark ? 'bg-white/[0.03] border-white/8 hover:border-blue-500/30 cursor-pointer' : 'bg-white border-slate-100 hover:border-blue-200 cursor-pointer'
                  }`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: isUpcoming ? 0.4 : 1, y: 0 }}
                  transition={{ delay: 0.1 + idx * 0.03, type: "spring", stiffness: 200 }}
                  whileHover={!isUpcoming ? { scale: 1.02, y: -1 } : {}}
                  whileTap={!isUpcoming ? { scale: 0.98 } : {}}
                >
                  {isSelected && (
                    <motion.div
                      className="absolute inset-0 rounded-xl border-2 border-blue-400/40"
                      layoutId="monthHighlight"
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    />
                  )}
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    m.status === 'completed'
                      ? isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-500'
                      : m.status === 'active'
                        ? isSelected ? 'bg-blue-500 !text-white shadow-sm' : isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-500'
                        : isDark ? 'bg-white/5 text-white/25' : 'bg-slate-50 text-slate-300'
                  }`}>
                    {m.status === 'completed' ? (
                      <Check size={13} strokeWidth={3} />
                    ) : m.status === 'active' ? (
                      <Target size={13} />
                    ) : (
                      <Lock size={11} />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className={`text-[12px] font-black uppercase tracking-wider leading-none ${
                      isSelected
                        ? isDark ? 'text-blue-400' : 'text-blue-600'
                        : isDark ? 'text-white/70' : 'text-slate-700'
                    }`}>
                      {m.name.slice(0, 3)}
                    </span>
                    <span className={`text-[10px] font-semibold leading-snug mt-0.5 ${isDark ? 'text-white/35' : 'text-slate-400'}`}>
                      {(m.score != null && parseFloat(m.score) > 0) ? `${m.score}/10`
                        : m.status === 'active' ? (isReviewWindowActive ? (m.allGoalsApproved ? 'Eval' : 'Review') : isEvaluationWindowActive ? 'Eval' : 'Active')
                        : m.status === 'completed' ? 'Done' : 'Locked'}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
        )}

        {/* Operational Goals Table */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="goals-skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <GoalsTableSkeleton />
            </motion.div>
          ) : (
            <motion.div
              key="goals-table"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <GoalsTable
                goals={currentMonthGoals || []}
                onApprove={onApproveGoal}
                onReject={onRejectGoal}
                onEdit={onEditGoal}
                onDelete={onDeleteGoal}
                onSetAchievedPercent={onSetAchievedPercent}
                canAddGoal={true}
                onAddGoal={onAddGoal}
                isGoalsCycleEditable={memberReportMonth === currentCycle?.month}
                inlineNewGoal={inlineNewGoal}
                setInlineNewGoal={setInlineNewGoal}
                editingGoalId={editingGoalId}
                setEditingGoalId={setEditingGoalId}
                onSaveNewGoal={onSaveNewGoal}
                onSaveEditGoal={onSaveEditGoal}
                onCancelEdit={onCancelEdit}
                minGoals={minGoals}
                maxGoals={maxGoals}
                criticalRequired={criticalRequired || 0}
                minWeightage={minWeightage}
                maxWeightage={maxWeightage}
                isManager={true}
                isViewingTeamMember={true}
                currentUserId={managerUserId}
                cycleId={currentCycle?._id || null}
                targetUserId={selectedMember?._id || selectedMember?.id || null}
                currentCycle={currentCycle}
                onSubmitGoals={onSubmitGoals}
                scoreRanges={scoreRanges}
                isSubmissionWindowActive={isSubmissionWindowActive}
                isReviewWindowActive={isReviewWindowActive}
                isEvaluationWindowActive={isEvaluationWindowActive}
                isOrgAdmin={isOrgAdmin}
                isAutonomousMode={isAutonomousMode}
                onToggleCritical={onToggleCritical}
                aiGoalEnabled={aiGoalEnabled}
                onToggleAiGoal={onToggleAiGoal}
                projectsList={projectsList}
                softSkillsData={softSkillsData}
                reviewCompletedAt={reviewCompletedAt}
                onMarkReviewComplete={onMarkReviewComplete}
                isMarkingReviewComplete={isMarkingReviewComplete}
                onSelfEvaluate={onSelfEvaluate}
                onVerifyGoal={onVerifyGoal}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Operational Goals Grid (Card Style) - Hidden, keeping for reference */}
        {false && (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            {currentMonthGoals.map((goal, idx) => (
              <motion.div
                key={goal.id}
                className={`bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm group relative overflow-hidden text-left ${isDark
                  ? 'bg-white/5 border-white/10 hover:border-white/20'
                  : 'bg-white border-slate-100 hover:border-slate-200'
                  }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.4 + (idx * 0.05),
                  duration: 0.3,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ scale: 1.02, y: -4, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex justify-between items-start mb-6">
                  <span className={`text-[11px] font-black ${isDark ? 'text-white/30' : 'text-slate-800'
                    }`}>
                    #0{idx + 1}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {goal.critical && (
                      <span className="px-2 py-0.5 bg-red-500 !text-white text-[7px] font-black uppercase rounded-lg tracking-widest">
                        Critical
                      </span>
                    )}
                    {goal.addedByManager && (
                      <span className={`px-2 py-0.5 border text-[7px] font-black uppercase rounded-lg tracking-widest ${isDark
                        ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                        : 'bg-blue-50 text-blue-500 border-blue-100'
                        }`}>
                        Manager
                      </span>
                    )}
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'edit',
                            label: (
                              <div className="flex items-center gap-2 px-2 py-1">
                                <Edit3 size={14} />
                                <span>Edit Goal</span>
                              </div>
                            ),
                            onClick: () => onEditGoal(goal.id),
                            className: 'hover:bg-slate-50 dark:hover:bg-white/5 transition-colors',
                          },
                          {
                            key: 'delete',
                            label: (
                              <div className="flex items-center gap-2 px-2 py-1 text-red-600 group/delete">
                                <Trash2 size={14} className="group-hover/delete:text-white transition-colors" />
                                <span className="group-hover/delete:text-white transition-colors">Delete Goal</span>
                              </div>
                            ),
                            onClick: () => onRejectGoal(goal.id),
                            danger: true,
                            className: 'hover:bg-red-600 hover:!text-white transition-all duration-200',
                          },
                        ],
                      }}
                      trigger={['click']}
                      placement="bottomRight"
                      overlayClassName="goals-dropdown-menu"
                    >
                      <button
                        className={`p-1.5 rounded-lg transition-all ${isDark
                          ? 'hover:bg-white/10 text-white/40 hover:text-white/60'
                          : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                          }`}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </Dropdown>
                  </div>
                </div>
                <h3 className={`text-sm font-black mb-2 leading-tight ${isDark ? '!text-white' : 'text-slate-900'
                  }`}>
                  {goal.name}
                </h3>
                <p className={`text-[10px] font-bold uppercase tracking-widest mb-6 ${isDark ? 'text-white/60' : 'text-slate-400'
                  }`}>
                  {goal.metric}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  <div className={`p-3 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-slate-50'
                    }`}>
                    <span className={`block text-[8px] font-black uppercase tracking-widest mb-0.5 ${isDark ? 'text-white/40' : 'text-slate-300'
                      }`}>
                      WEIGHTAGE
                    </span>
                    <span className={`text-xs font-black ${isDark ? '!text-white' : 'text-slate-900'
                      }`}>
                      {goal.weight}
                    </span>
                  </div>
                  <div className={`p-3 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-slate-50'
                    }`}>
                    <span className={`block text-[8px] font-black uppercase tracking-widest mb-0.5 ${isDark ? 'text-white/40' : 'text-slate-300'
                      }`}>
                      SCORE
                    </span>
                    <span className={`text-xs font-black ${isDark ? 'text-blue-400' : 'text-blue-600'
                      }`}>
                      {goal.score || '---'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onApproveGoal(goal.id)}
                    disabled={goal.status === "Approved"}
                    className={`flex-1 py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all duration-200 flex items-center justify-center gap-2 ${goal.status === "Approved"
                      ? 'bg-emerald-100 text-emerald-700 cursor-not-allowed'
                      : 'bg-green-50 text-green-600 hover:bg-green-600 hover:!text-white active:scale-95'
                      }`}
                  >
                    <Check size={12} strokeWidth={4} /> APPROVE
                  </button>
                  <button
                    onClick={() => onRejectGoal(goal.id)}
                    className="flex-1 py-3 bg-red-50 text-red-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-red-600 hover:!text-white transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
                  >
                    <X size={12} strokeWidth={4} /> REJECT
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Soft Skills & Review Summary - always visible when soft skills are enabled */}
        <AnimatePresence mode="wait">
          {softSkillsEnabled && (softSkillsLoading ? (
            <motion.div
              key="soft-skills-skeleton"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="mt-6"
            >
              <SoftSkillsSkeleton />
            </motion.div>
          ) : (
            <motion.div
              key="soft-skills"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.1, type: "spring", stiffness: 100 }}
              className="mt-6"
            >
              <SoftSkillsSection
                softSkillsData={softSkillsData}
                onRatingChange={onSoftSkillRatingChange}
                canEditEmployeeRating={canEditEmployeeRating}
                canEditManagerRating={canEditManagerRating}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        <AnimatePresence mode="wait">
          {softSkillsEnabled && (reviewSummaryLoading ? (
            <motion.div
              key="review-skeleton"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="mt-6"
            >
              <ReviewSummarySkeleton />
            </motion.div>
          ) : (
            <motion.div
              key="review-summary"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.2, type: "spring", stiffness: 100 }}
              className="mt-6"
            >
              {/* <ReviewSummary
                reviewSummary={reviewSummary}
                selectedMonth={memberReportMonth}
                isExpanded={isReviewSummaryExpanded}
                onToggle={onToggleReviewSummary}
                onReviewChange={onReviewChange}
                canEditEmployeeResponse={canEditEmployeeResponse}
                canEditManagerResponse={canEditManagerResponse}
              /> */}
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Additional Achievements Section */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.4, type: "spring", stiffness: 100 }}
          className={`border rounded-[2.5rem] overflow-hidden shadow-sm mt-6 ${
            isDark
              ? 'bg-white/5 border-white/10'
              : 'bg-white border-slate-200'
          }`}
        >
          <div className={`p-4 sm:p-8 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 ${
            isDark
              ? 'bg-white/5 border-white/10'
              : 'bg-[#fafbff] border-slate-100'
          }`}>
            <div className="flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-amber-500">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
              </svg>
              <h2 className={`text-[14px] font-black uppercase tracking-wider leading-none ${
                isDark ? '!text-white' : 'text-slate-900'
              }`}>
                ADDITIONAL ACHIEVEMENTS
              </h2>
              <div className="w-3.5 h-3.5 rounded-full bg-amber-400/20 flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              </div>
            </div>
            <button
              onClick={onAddAchievement}
              className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-colors shadow-lg ${
                isDark
                  ? 'bg-slate-900 !text-white hover:bg-blue-600 shadow-slate-900/10'
                  : 'bg-slate-900 text-white hover:bg-blue-600 shadow-slate-900/10'
              }`}
            >
              <Plus size={14} strokeWidth={3} />
              Add Achievement
            </button>
          </div>

          <div className="p-8">
            {achievements.length > 0 ? (
              <ul className="space-y-4">
                {achievements.map((achievement) => (
                  <li
                    key={achievement.id}
                    className={`p-4 rounded-2xl border ${
                      isDark
                        ? 'bg-white/5 border-white/10'
                        : 'bg-slate-50 border-slate-100'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className={`text-sm font-black mb-1 ${
                          isDark ? '!text-white' : 'text-slate-900'
                        }`}>
                          {achievement.title}
                        </h3>
                        {achievement.description && (
                          <p className={`text-xs leading-relaxed ${
                            isDark ? 'text-white/80' : 'text-slate-600'
                          }`}>
                            {achievement.description}
                          </p>
                        )}
                      </div>
                      {achievement.date && (
                        <span className={`text-[10px] font-black uppercase tracking-widest whitespace-nowrap ${
                          isDark ? 'text-white/60' : 'text-slate-400'
                        }`}>
                          {achievement.date}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className={`col-span-full py-12 flex flex-col items-center justify-center border-2 border-dashed rounded-[2.5rem] ${
                isDark
                  ? 'border-white/10'
                  : 'border-slate-100'
              }`}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={isDark ? 'text-white/20' : 'text-slate-100'}>
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
                <p className={`text-sm font-black uppercase tracking-widest mt-4 ${
                  isDark ? 'text-white/60' : 'text-slate-400'
                }`}>
                  No additional achievements yet.
                </p>
                <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${
                  isDark ? 'text-white/40' : 'text-slate-300'
                }`}>
                  Click "Add Achievement" to record one.
                </p>
              </div>
            )}
          </div>
        </motion.div> */}
      </motion.div>
    );
  }

  // Team Dashboard View
  return (
    <div className="space-y-4" style={{ fontFamily: '-apple-system, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif' }}>
      {/* Header with Goal Dashboard Tab and View Team Member Button */}
      {/* <div className={`px-6 py-2 border-b flex items-center justify-between ${
        isDark ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-slate-100'
      }`}>
        <button
          onClick={() => setManagerTab('goals')}
          className={`text-[11px] font-black uppercase tracking-widest flex items-center gap-2 py-2 border-b-2 transition-all ${
            managerTab === 'goals'
              ? isDark
                ? 'border-blue-400 text-blue-400'
                : 'border-blue-600 text-blue-600'
              : isDark
                ? 'border-transparent text-white/60 hover:text-white'
                : 'border-transparent text-slate-400 hover:text-slate-600'
          }`}
        >
          <LayoutGrid size={14} />
          Goal Dashboard
        </button>
        <button
          onClick={() => setManagerTab('member')}
          className={`text-[11px] font-black uppercase tracking-widest flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            isDark
              ? managerTab === 'member'
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-white/5 text-white/80 hover:bg-white/10 border border-white/10'
              : managerTab === 'member'
                ? 'bg-blue-50 text-blue-600 border border-blue-200'
                : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <UserCheck size={14} />
          View Team Member
        </button>
      </div> */}

      <AnimatePresence mode="wait">
        {managerTab === 'goals' ? (
          // Goal Dashboard or Detail View - Same as Employee View
          !isManagerDetailView ? (
            <motion.div
              key="goals-dashboard"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 100 }}
            >
              <GoalsDashboard
                bannerConfig={bannerConfig}
                months={months}
                onMonthClick={onMonthClick}
                onAddGoal={onAddGoal}
                badgeRanges={badgeRanges}
                streakIconUrl={streakIconUrl}
              />
            </motion.div>
          ) : (
            <motion.div
              key="goals-detail"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 100 }}
            >
              <GoalsDetailView
                softSkillsEnabled={softSkillsEnabled}
                selectedMonth={memberReportMonth}
                selectedYear={selectedYear}
                bannerConfig={bannerConfig}
                goals={currentMonthGoals}
                softSkillsData={softSkillsData}
                reviewSummary={reviewSummary}
                isReviewSummaryExpanded={isReviewSummaryExpanded}
                onToggleReviewSummary={onToggleReviewSummary}
                onReviewChange={onReviewChange}
                onBack={() => {
                  setIsManagerDetailView(false);
                  setMemberReportMonth(currentCycle?.month || null);
                }}
                onAddGoal={onAddGoal}
                onApproveGoal={onApproveGoal}
                onRejectGoal={onRejectGoal}
                onEditGoal={onEditGoal}
                onSetAchievedPercent={onSetAchievedPercent}
                onSoftSkillRatingChange={onSoftSkillRatingChange}
                achievements={achievements}
                onAddAchievement={onAddAchievement}
                minGoals={minGoals}
                maxGoals={maxGoals}
                criticalRequired={criticalRequired || 0}
                minWeightage={minWeightage}
                maxWeightage={maxWeightage}
                isManager={true}
                isViewingTeamMember={true}
                inlineNewGoal={inlineNewGoal}
                setInlineNewGoal={setInlineNewGoal}
                editingGoalId={editingGoalId}
                setEditingGoalId={setEditingGoalId}
                onSaveNewGoal={onSaveNewGoal}
                onSaveEditGoal={onSaveEditGoal}
                onCancelEdit={onCancelEdit}
                isGoalsCycleEditable={true}
                currentCycle={currentCycle}
                isEvaluationWindowActive={isEvaluationWindowActive}
                isOrgAdmin={isOrgAdmin}
                isAutonomousMode={isAutonomousMode}
                badgeRanges={badgeRanges}
                streakIconUrl={streakIconUrl}
                onToggleCritical={onToggleCritical}
                aiGoalEnabled={aiGoalEnabled}
                onToggleAiGoal={onToggleAiGoal}
                projectsList={projectsList}
                onSelfEvaluate={onSelfEvaluate}
                onVerifyGoal={onVerifyGoal}
              />
            </motion.div>
          )
        ) : (
          // Team Members View
          <motion.div
            key="team-members"
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3, type: "spring", stiffness: 100 }}
          >
            {/* New mock-aligned header + KPI strip — fully dynamic.
                Prefer scope-aware teamStats when available; fall back to derived values from
                the paginated direct-only list during the brief loading window. */}
            <TeamMembersHeader
              totalMembers={teamStats ? teamStats.totalMembers : filteredMembers.length}
              submittedCount={teamStats ? teamStats.submittedCount : (submissionCounts?.submitted || 0)}
              notSubmittedCount={teamStats ? teamStats.notSubmittedCount : (submissionCounts?.notSubmitted || 0)}
              teamAvgScore={teamStats ? teamStats.avgScore : teamAvgScore}
              cycleEndDate={currentCycle?.reviewEnd || currentCycle?.submissionEnd || null}
              isReviewWindowActive={isReviewWindowActive}
              reporteeScope={reporteeScope}
              setReporteeScope={setReporteeScope}
              showScopeToggle={hasIndirectReports}
            />


            {/* Filter bar — same tg-filterbar / ToolSelect pattern as Projects/Team */}
            <div className="tg-filterbar">
              <label className="tg-tool tg-tool-search">
                <Search size={14} />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search team member"
                  aria-label="Search team member"
                />
              </label>
              <span className="tg-tool-sep" />
              <ToolSelect
                icon={<Filter size={14} />}
                value={goalFilter}
                onChange={(v) => setGoalFilter(v ?? 'all')}
                options={[
                  { value: 'all', label: `All (${submissionCounts?.all ?? ((teamMembers || []).length)})` },
                  { value: 'submitted', label: `Submitted (${submissionCounts?.submitted ?? 0})` },
                  { value: 'not_submitted', label: `Not Submitted (${submissionCounts?.notSubmitted ?? 0})` },
                ]}
                panelWidth={190}
              />
            </div>

            {/* New mock-aligned members table — fully dynamic */}
            <TeamMembersTable
              members={filteredMembers}
              badgeRanges={badgeRanges}
              cycleEndDate={currentCycle?.reviewEnd || currentCycle?.submissionEnd || null}
              cycleId={currentCycle?._id || null}
              showHierarchy={reporteeScope === 'all'}
              onMemberClick={(m) => setSelectedMember(m)}
              pagination={pagination}
              currentPage={currentPage}
              pageSize={12}
              onPageChange={onPageChange}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManagerView;
